/**
 * Drafts.jsx
 * Posts you have started but not sent.
 *
 * A post used to exist only for the instant between "Review" and "Publish",
 * so anything you began had to be finished in one sitting. Drafts are now
 * first-class: start on Monday, finish on Tuesday.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import MediaPreview from '../components/MediaPreview.jsx';
import { Plus, Warning, Calendar as CalendarIcon, Tag, Check } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

export default function Drafts() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // Tidying up is a deliberate mode rather than a checkbox always on show:
  // the common visit is "open one and finish it", not "clear the decks".
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState(null);
  const [progress, setProgress] = useState(null);

  const load = useCallback(async () => {
    try {
      const { posts } = await api.posts.list({ status: 'draft' });
      setDrafts(posts);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(post) {
    if (!confirm('Delete this draft? It has not been published anywhere.')) return;
    setBusy(true);
    try {
      await api.posts.remove(post.id);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  const allPicked = drafts.length > 0 && picked.length === drafts.length;

  /** Leaves selection mode and forgets what was ticked. */
  function stopPicking() {
    setPicking(false);
    setPicked([]);
    setProgress(null);
  }

  /**
   * Deletes everything ticked.
   *
   * One at a time rather than in a batch, because a draft that refuses to go
   * — one that turns out to be part-published — must not take the other forty
   * with it. Each failure is counted and named at the end.
   */
  async function removePicked() {
    const chosen = drafts.filter((d) => picked.includes(d.id));
    if (chosen.length === 0) return;

    // Deliberately wordy. There is no undo, and "Delete 11 drafts" is a very
    // different sentence from "Delete this draft".
    const sure = confirm(
      `Delete ${chosen.length} draft${chosen.length === 1 ? '' : 's'}?

` +
        'This cannot be undone. Nothing here has been published, so nothing will be ' +
        'removed from Facebook or Instagram.',
    );
    if (!sure) return;

    setBusy(true);
    setError(null);
    setNote(null);

    let done = 0;
    const failed = [];

    for (const draft of chosen) {
      try {
        await api.posts.remove(draft.id);
      } catch (e) {
        failed.push(`${draft.brand_name}: ${e.message}`);
      }
      setProgress({ done: ++done, total: chosen.length });
    }

    await load();
    stopPicking();
    setBusy(false);

    const gone = chosen.length - failed.length;
    if (failed.length === 0) {
      setNote(`${gone} draft${gone === 1 ? '' : 's'} deleted.`);
    } else {
      setError(
        new Error(
          `${gone} of ${chosen.length} deleted. ${failed.slice(0, 3).join('; ')}` +
            `${failed.length > 3 ? ` and ${failed.length - 3} more` : ''}.`,
        ),
      );
    }
  }

  if (loading) return <p className="mono">Loading…</p>;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Drafts</div>
          <h1>Unfinished posts</h1>
          <p>Started but not sent. Pick one up where you left it.</p>
        </div>
        <div className="page-head__actions page-head__actions--stacked">
          <button className="btn btn--primary" onClick={() => navigate('/create')}>
            <Plus /> New post
          </button>

          {drafts.length > 0 &&
            (picking ? (
              <button className="btn" onClick={stopPicking} disabled={busy}>
                Cancel
              </button>
            ) : (
              <button className="btn" onClick={() => setPicking(true)}>
                <Check size={15} /> Select &amp; delete
              </button>
            ))}
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {note && (
        <div className="notice notice--info">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {progress && (
        <div className="notice notice--info">
          <Check />
          <span>
            Deleting {progress.done} of {progress.total}…
          </span>
        </div>
      )}

      {picking && drafts.length > 0 && (
        <div className="card">
          <div className="row">
            <div>
              <div className="row__label">
                {picked.length === 0
                  ? 'Nothing selected'
                  : `${picked.length} of ${drafts.length} selected`}
              </div>
              <div className="row__sub">
                Tick the ones to remove. Deleting cannot be undone.
              </div>
            </div>
            <div className="row__end">
              <button
                className="btn btn--sm"
                onClick={() => setPicked(allPicked ? [] : drafts.map((d) => d.id))}
                disabled={busy}
              >
                {allPicked ? 'Untick all' : `Select all ${drafts.length}`}
              </button>
              <button
                className="btn btn--sm btn--danger"
                onClick={removePicked}
                disabled={busy || picked.length === 0}
              >
                Delete {picked.length > 0 ? picked.length : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {drafts.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty__art">
              <CalendarIcon size={30} />
            </div>
            <h3>No drafts</h3>
            <p>Anything you save on the Create screen without publishing appears here.</p>
            <button className="btn btn--primary" onClick={() => navigate('/create')}>
              <Plus /> Write a post
            </button>
          </div>
        </div>
      ) : (
        <section className="card">
          <div className="rows">
            {drafts.map((d) => (
              <div
                className={`row${picking && picked.includes(d.id) ? ' is-picked' : ''}`}
                key={d.id}
              >
                {picking && (
                  <input
                    type="checkbox"
                    checked={picked.includes(d.id)}
                    onChange={() => toggle(d.id)}
                    disabled={busy}
                    aria-label={`Select this ${d.brand_name} draft`}
                  />
                )}

                {d.media_id ? (
                  <MediaPreview
                    media={{ id: d.media_id, kind: 'image' }}
                    className="row__thumb"
                  />
                ) : (
                  <span className="tile tile--sm">
                    <Tag size={17} />
                  </span>
                )}

                <div style={{ minWidth: 0 }}>
                  <div className="row__label">
                    {d.brand_name}
                    {d.platforms ? ` · ${d.platforms.split(',').join(' + ')}` : ' · no platform'}
                  </div>
                  <div className="row__sub">
                    {d.preview ? `${d.preview.slice(0, 90)}…` : 'No caption yet'}
                  </div>
                  <div className="row__sub">Edited {formatLondon(d.updated_at)}</div>
                </div>

                {!picking && (
                  <div className="row__end">
                    <button
                      className="btn btn--sm btn--primary"
                      onClick={() => navigate(`/create?post=${d.id}`)}
                    >
                      Open
                    </button>
                    <button
                      className="btn btn--sm btn--danger"
                      onClick={() => remove(d)}
                      disabled={busy}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
